/**
 * Love Store — Sprint 16 Redesign
 *
 * Store operations for love entities.
 * Follows patterns from entity-sync.ts for consistency.
 *
 * Namespaces:
 *   loves/{userId}/things/{entityId}       → LoveEntity
 *   loves/{userId}/experiences/{entityId}  → LoveEntity
 *   loves/{userId}/people/{entityId}       → LoveEntity
 */
import { NS } from '@ownyou/shared-types';
import { emptySourceBreakdown, emptyProxyBreakdown } from '../types/love-entity';
/** Maximum signals to keep per entity */
const MAX_SIGNALS_PER_ENTITY = 20;
/**
 * Get the namespace tuple for a love entity type.
 */
function getNamespace(userId, type) {
    switch (type) {
        case 'thing': return NS.loveThings(userId);
        case 'experience': return NS.loveExperiences(userId);
        case 'person': return NS.lovePeople(userId);
    }
}
/**
 * Upsert a love entity into the Store.
 * If entity exists, merges signals and updates confidence.
 */
export async function upsertLoveEntity(userId, entity, store) {
    const namespace = getNamespace(userId, entity.type);
    // Cap signals at MAX_SIGNALS_PER_ENTITY (keep most recent)
    const trimmedEntity = {
        ...entity,
        signals: entity.signals
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, MAX_SIGNALS_PER_ENTITY),
    };
    await store.put(namespace, entity.id, trimmedEntity);
}
/**
 * Get a love entity by ID.
 */
export async function getLoveEntity(userId, type, entityId, store) {
    try {
        const namespace = getNamespace(userId, type);
        const result = await store.get(namespace, entityId);
        if (!result)
            return undefined;
        return deserializeEntity(result);
    }
    catch {
        return undefined;
    }
}
/**
 * List all love entities of a given type.
 */
export async function listLoveEntities(userId, type, store) {
    try {
        const namespace = getNamespace(userId, type);
        const result = await store.list(namespace);
        return result.items
            .filter(rec => !rec._deleted)
            .map(rec => deserializeEntity(rec));
    }
    catch {
        return [];
    }
}
/**
 * List ALL love entities across all types.
 */
export async function listAllLoveEntities(userId, store) {
    const [things, experiences, people] = await Promise.all([
        listLoveEntities(userId, 'thing', store),
        listLoveEntities(userId, 'experience', store),
        listLoveEntities(userId, 'person', store),
    ]);
    return [...things, ...experiences, ...people];
}
/**
 * Delete a love entity by overwriting with a tombstone.
 * MemoryStore doesn't have a delete method, so we use put with a marker.
 */
export async function deleteLoveEntity(userId, type, entityId, store) {
    const namespace = getNamespace(userId, type);
    // Overwrite with empty/tombstone record (filtered out on read)
    await store.put(namespace, entityId, { _deleted: true });
}
/**
 * Add new signals to an existing entity, updating confidence and breakdowns.
 */
export async function addSignalsToEntity(userId, entityId, type, newSignals, newConfidence, store) {
    const existing = await getLoveEntity(userId, type, entityId, store);
    if (!existing)
        return undefined;
    // Merge signals (new first, then existing, capped at MAX)
    const mergedSignals = [...newSignals, ...existing.signals]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, MAX_SIGNALS_PER_ENTITY);
    // Recalculate breakdowns
    const sourceBreakdown = emptySourceBreakdown();
    const proxyBreakdown = emptyProxyBreakdown();
    for (const signal of mergedSignals) {
        sourceBreakdown[signal.source]++;
        proxyBreakdown[signal.proxy]++;
    }
    const updated = {
        ...existing,
        confidence: newConfidence,
        lastValidatedAt: Date.now(),
        signals: mergedSignals,
        sourceBreakdown,
        proxyBreakdown,
        lastSignalAt: mergedSignals.length > 0 ? mergedSignals[0].timestamp : existing.lastSignalAt,
    };
    await upsertLoveEntity(userId, updated, store);
    return updated;
}
// ---------------------------------------------------------------------------
// Serialization helpers
// ---------------------------------------------------------------------------
function deserializeEntity(val) {
    const actionCtx = val.actionableContext ?? {};
    return {
        id: String(val.id ?? ''),
        type: val.type ?? 'thing',
        name: String(val.name ?? ''),
        category: String(val.category ?? ''),
        subcategories: val.subcategories ?? [],
        confidence: Number(val.confidence ?? 0),
        lastValidatedAt: Number(val.lastValidatedAt ?? Date.now()),
        signals: val.signals ?? [],
        sourceBreakdown: val.sourceBreakdown ?? emptySourceBreakdown(),
        proxyBreakdown: val.proxyBreakdown ?? emptyProxyBreakdown(),
        firstSeenAt: Number(val.firstSeenAt ?? Date.now()),
        lastSignalAt: Number(val.lastSignalAt ?? Date.now()),
        trend: val.trend ?? 'new',
        actionableContext: {
            location: actionCtx.location,
            budget: actionCtx.budget,
            socialContext: actionCtx.socialContext,
            timing: actionCtx.timing,
            frequency: actionCtx.frequency,
            constraints: actionCtx.constraints,
            suggestedActions: actionCtx.suggestedActions ?? [],
            relatedEntityIds: actionCtx.relatedEntityIds ?? [],
        },
        reasoning: String(val.reasoning ?? ''),
        proxyEvidence: val.proxyEvidence ?? {},
        nature: val.nature,
        ikigaiRelevance: val.ikigaiRelevance ?? { love: 0, goodAt: 0, worldNeeds: 0, paidFor: 0 },
        stability: Number(val.stability ?? 0.5),
        temporalPattern: val.temporalPattern,
        deadline: val.deadline,
        urgencyReason: val.urgencyReason,
        pass1Model: String(val.pass1Model ?? ''),
        pass2Model: String(val.pass2Model ?? ''),
        detectionRunId: String(val.detectionRunId ?? ''),
        dualConfidence: val.dualConfidence,
    };
}
//# sourceMappingURL=love-store.js.map